import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { alertApiRef } from '@backstage/core-plugin-api';
import { Folder } from './ArtifactTable'; // import Folder type

export type ArtifactType = 'binary_file' | 'tabular_data' | 'document' | 'source_code' | 'image' | 'configuration' | 'model_weights' | 'dataset';
export type UseCaseTag = 'UC1' | 'UC2' | 'UC3' | '';

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
  folderId?: string | null;
  folders?: Folder[]; // optional list of existing folders for selection
}

export const UploadArtifactDialog = ({ open, onClose, onUploaded, folderId, folders }: Props) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [artifactType, setArtifactType] = useState<ArtifactType>('binary_file');
  const [category, setCategory] = useState('');
  const [useCaseTag, setUseCaseTag] = useState<UseCaseTag>('');
  const [uploading, setUploading] = useState(false);

  const resolvedFolderId = folderId ?? null;

  const destinationLabel = useMemo(() => {
    if (!resolvedFolderId) return 'Root (top-level)';
    const folder = folders?.find(f => f.id === resolvedFolderId);
    return folder?.name ?? 'Selected folder';
  }, [resolvedFolderId, folders]);

  const resetForm = () => {
    setSelectedFile(null);
    setName('');
    setDescription('');
    setArtifactType('binary_file');
    setCategory('');
    setUseCaseTag('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (!uploading) {
      resetForm();
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file && !name) {
      // Pre-fill name from filename (without extension)
      setName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !name || !artifactType) {
      alertApi.post({ message: 'Please fill in all required fields and select a file.', severity: 'warning' });
      return;
    }

    setUploading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', name);
      formData.append('description', description);
      formData.append('artifact_type', artifactType);
      formData.append('category', category);
      if (useCaseTag) formData.append('use_case_tag', useCaseTag);
      // Destination is chosen before this dialog opens.
      if (resolvedFolderId) formData.append('folder_id', resolvedFolderId);

      const response = await fetch(`${baseUrl}/artifacts`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Upload failed');
      }

      alertApi.post({ message: 'Artifact uploaded successfully.', severity: 'success' });
      // Audit check: the backend audit middleware should have logged this upload.
      console.debug('Upload should be recorded in audit log');
      resetForm();
      onUploaded();
      onClose();
    } catch (err: any) {
      alertApi.post({ message: `Upload failed: ${err.message}`, severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Artifact</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        {uploading && <LinearProgress />}

        <Button variant="outlined" component="label" disabled={uploading}>
          {selectedFile ? selectedFile.name : 'Choose File *'}
          <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
        </Button>
        {selectedFile && (
          <Typography variant="caption" color="textSecondary">
            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
          </Typography>
        )}

        <Typography variant="caption" color="textSecondary">
          Upload destination: {destinationLabel}
        </Typography>

        <TextField
          label="Name *"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={uploading}
          variant="outlined"
          size="small"
          fullWidth
        />

        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={uploading}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={2}
        />

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Artifact Type *</InputLabel>
          <Select
            value={artifactType}
            onChange={e => setArtifactType(e.target.value as ArtifactType)}
            label="Artifact Type *"
            disabled={uploading}
          >
            <MenuItem value="binary_file">Binary File (model, firmware, dataset)</MenuItem>
            <MenuItem value="tabular_data">Tabular Data (CSV, Parquet)</MenuItem>
            <MenuItem value="document">Document / JSON</MenuItem>
            <MenuItem value="source_code">Source Code (MATLAB, Python, C++)</MenuItem>
            <MenuItem value="image">Image (PNG, JPG, TIFF)</MenuItem>
            <MenuItem value="configuration">Configuration (YAML, INI, TOML)</MenuItem>
            <MenuItem value="model_weights">Model Weights (ONNX, PT, PTH)</MenuItem>
            <MenuItem value="dataset">Dataset (HDF5, Feather, Arrow)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Category (e.g. ml-model, firmware, sensor-data)"
          value={category}
          onChange={e => setCategory(e.target.value)}
          disabled={uploading}
          variant="outlined"
          size="small"
          fullWidth
        />

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Use Case Tag</InputLabel>
          <Select
            value={useCaseTag}
            onChange={e => setUseCaseTag(e.target.value as UseCaseTag)}
            label="Use Case Tag"
            disabled={uploading}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="UC1">UC1 – Data-Driven EV Optimisation</MenuItem>
            <MenuItem value="UC2">UC2 – EV Health Monitoring & Predictive Maintenance</MenuItem>
            <MenuItem value="UC3">UC3 – Smart Motion Control</MenuItem>
          </Select>
        </FormControl>

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button onClick={handleUpload} color="primary" variant="contained" disabled={uploading || !selectedFile || !name}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
